import { useEffect, useRef, useState } from "react";
import {
    Camera, ChevronDown, ChevronUp, Check, Loader2, Receipt, X,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { DviAPI, AttachmentAPI, extractError, buildSignedUrl } from "@/lib/api";
import { SeverityPicker } from "./SeverityPicker";

const SAVE_DEBOUNCE_MS = 400;
const MAX_PHOTOS_PER_FINDING = 5;

function pickAttachmentSignedUrl(att) {
    return att.signed_url || att.url || (att.id ? buildSignedUrl(`/api/files/${att.id}`) : "");
}

export function FindingCard({
    item,
    section,
    finding,
    inspectionId,
    jobcardId,
    canWrite,
    canConvert,
    locked,
    onFindingSaved,
    onConverted,
}) {
    const sev = finding?.severity || null;
    const findingId = finding?.id || null;
    const [notes, setNotes] = useState(finding?.technician_notes || "");
    const [recommended, setRecommended] = useState(finding?.recommended_action || "");
    const [costRupees, setCostRupees] = useState(
        finding?.estimated_cost_minor ? String(finding.estimated_cost_minor / 100) : "",
    );
    const [lastSyncedId, setLastSyncedId] = useState(findingId);
    const [expanded, setExpanded] = useState(!!sev && (sev === "yellow" || sev === "red"));
    const [savingState, setSavingState] = useState("idle"); // idle | saving | saved | error
    const [uploading, setUploading] = useState(false);
    const [converting, setConverting] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const debounceRef = useRef(null);
    const findingRef = useRef(finding);

    // React-19-pattern: sync state with new server-confirmed finding identity
    // (cheap if the same finding, ignored if user is mid-edit and id matches).
    if (findingId !== lastSyncedId) {
        setLastSyncedId(findingId);
        setNotes(finding?.technician_notes || "");
        setRecommended(finding?.recommended_action || "");
        setCostRupees(
            finding?.estimated_cost_minor ? String(finding.estimated_cost_minor / 100) : "",
        );
    }
    useEffect(() => { findingRef.current = finding; }, [finding]);
    // Keep a ref to the latest local input state so the debounced/queued
    // _saveNow always sees the freshest values (avoiding stale-closure bugs).
    const localRef = useRef({ notes, recommended, costRupees, sev });
    localRef.current = { notes, recommended, costRupees, sev };

    async function _saveNow(patchFields) {
        if (!canWrite || locked) return;
        setSavingState("saving");
        try {
            const cur = localRef.current;
            const body = {
                section_id: section.id,
                item_id: item.id,
                severity: patchFields.severity ?? cur.sev,
                technician_notes: patchFields.technician_notes ?? cur.notes ?? null,
                recommended_action: patchFields.recommended_action ?? cur.recommended ?? null,
                estimated_cost_minor: (() => {
                    const v = patchFields.estimated_cost_minor !== undefined
                        ? patchFields.estimated_cost_minor
                        : (cur.costRupees ? Math.round(parseFloat(cur.costRupees) * 100) : null);
                    return Number.isFinite(v) ? v : null;
                })(),
                attachment_ids: findingRef.current?.attachment_ids || [],
            };
            if (!body.severity) {
                setSavingState("idle");
                return;
            }
            const saved = await DviAPI.upsertFinding(inspectionId, body);
            setSavingState("saved");
            setTimeout(() => setSavingState((s) => (s === "saved" ? "idle" : s)), 1500);
            onFindingSaved?.(saved);
        } catch (err) {
            setSavingState("error");
            toast.error(extractError(err));
        }
    }

    function _debouncedSave(patchFields) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => _saveNow(patchFields), SAVE_DEBOUNCE_MS);
    }

    function handleSeverity(newSev) {
        if (!canWrite || locked) {
            toast.error(locked ? "Inspection locked." : "You don't have permission to edit findings.");
            return;
        }
        if (newSev === "yellow" || newSev === "red") setExpanded(true);
        _saveNow({ severity: newSev });
    }

    async function handlePhotoUpload(e) {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        const currentAtts = findingRef.current?.attachment_ids || [];
        if (currentAtts.length + files.length > MAX_PHOTOS_PER_FINDING) {
            toast.error(`Max ${MAX_PHOTOS_PER_FINDING} photos per finding`);
            return;
        }
        setUploading(true);
        try {
            const perFileMeta = files.map(() => ({ kind: "inspection", caption: item.label }));
            const result = await AttachmentAPI.upload(jobcardId, files, perFileMeta);
            const newIds = (result.items || result.uploaded || []).map((a) => a.id || a._id).filter(Boolean);
            if (!newIds.length) throw new Error("Upload returned no ids");
            await _saveNow({}); // ensure finding exists with current severity
            // Push attachments via the dedicated endpoint so backend validates jobcard scope
            const updated = await DviAPI.addFindingAttachments(finding.id, { attachment_ids: newIds });
            onFindingSaved?.(updated);
            toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} added`);
        } catch (err) {
            toast.error(extractError(err));
        } finally {
            setUploading(false);
        }
    }

    async function handleConvert() {
        if (!finding || !canConvert) return;
        const missing = [];
        if (!recommended.trim()) missing.push("Recommended action");
        if (!costRupees.trim() || !Number.isFinite(parseFloat(costRupees))) missing.push("Estimated cost");
        if (missing.length) {
            toast.error(`Fill ${missing.join(" and ")} before converting`);
            return;
        }
        setConverting(true);
        try {
            // Force a save first so the finding has the latest fields
            if (debounceRef.current) clearTimeout(debounceRef.current);
            await _saveNow({});
            const res = await DviAPI.convertFindingToEstimate(finding.id);
            toast.success(
                `Added to estimate · ₹${(res.line_total_minor / 100).toLocaleString("en-IN")}`,
            );
            onConverted?.(res, finding);
        } catch (err) {
            toast.error(extractError(err));
        } finally {
            setConverting(false);
        }
    }

    const isYellowRed = sev === "yellow" || sev === "red";
    const attachments = finding?.attachments || []; // server may inline these
    const attachmentIds = finding?.attachment_ids || [];
    const photoCount = attachments.length || attachmentIds.length;
    const alreadyConverted = !!finding?.converted_to_estimate_line_id;
    const testidPrefix = `finding-${item.id}`;

    return (
        <div
            data-testid={`${testidPrefix}-card`}
            data-severity={sev || "unset"}
            className={cn(
                "rounded-lg border bg-white p-3 transition-shadow sm:p-4 dark:bg-zinc-900",
                sev === "red"
                    ? "border-rose-300 dark:border-rose-700/50"
                    : sev === "yellow"
                    ? "border-amber-300 dark:border-amber-700/50"
                    : sev === "green"
                    ? "border-emerald-300 dark:border-emerald-700/50"
                    : "border-zinc-200 dark:border-zinc-800",
            )}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 font-medium leading-tight">
                        <span data-testid={`${testidPrefix}-label`}>{item.label}</span>
                        {item.is_required ? (
                            <span className="text-rose-500" title="Required" data-testid={`${testidPrefix}-required`}>*</span>
                        ) : null}
                    </div>
                    <div className="mt-0.5 text-xs text-zinc-500">
                        {item.requires_photo ? "Photo recommended" : "\u00a0"}
                    </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-500" aria-live="polite">
                    {savingState === "saving" && <Loader2 className="h-3 w-3 animate-spin" />}
                    {savingState === "saved" && <Check className="h-3 w-3 text-emerald-600" />}
                    {savingState === "saved" && <span data-testid={`${testidPrefix}-saved`}>Saved</span>}
                    {savingState === "error" && <X className="h-3 w-3 text-rose-600" />}
                </div>
            </div>

            <div className="mt-3">
                <SeverityPicker
                    value={sev}
                    onChange={handleSeverity}
                    disabled={!canWrite || locked}
                    testidPrefix={testidPrefix}
                />
            </div>

            {sev ? (
                <div className="mt-2">
                    <button
                        type="button"
                        onClick={() => setExpanded((e) => !e)}
                        data-testid={`${testidPrefix}-toggle`}
                        className="flex items-center gap-1 text-xs font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400"
                    >
                        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {expanded ? "Hide details" : "Add details"}
                    </button>
                </div>
            ) : null}

            {sev && expanded ? (
                <div className="mt-3 space-y-3 border-t border-zinc-100 pt-3 dark:border-zinc-800">
                    <Textarea
                        rows={2}
                        placeholder="Notes (visible internally + on customer PDF)"
                        value={notes}
                        onChange={(e) => {
                            setNotes(e.target.value);
                            _debouncedSave({ technician_notes: e.target.value });
                        }}
                        disabled={!canWrite || locked}
                        data-testid={`${testidPrefix}-notes`}
                        className="resize-none text-sm"
                    />

                    {isYellowRed ? (
                        <>
                            <Input
                                type="text"
                                placeholder="Recommended action (e.g. Replace pads)"
                                value={recommended}
                                onChange={(e) => {
                                    setRecommended(e.target.value);
                                    _debouncedSave({ recommended_action: e.target.value });
                                }}
                                disabled={!canWrite || locked}
                                data-testid={`${testidPrefix}-recommended`}
                                className="text-sm"
                            />
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-zinc-500">₹</span>
                                <Input
                                    type="number"
                                    inputMode="decimal"
                                    placeholder="Estimated cost"
                                    value={costRupees}
                                    onChange={(e) => {
                                        setCostRupees(e.target.value);
                                        const v = e.target.value ? Math.round(parseFloat(e.target.value) * 100) : null;
                                        _debouncedSave({ estimated_cost_minor: v });
                                    }}
                                    disabled={!canWrite || locked}
                                    data-testid={`${testidPrefix}-cost`}
                                    className="text-sm"
                                />
                            </div>
                        </>
                    ) : null}

                    {/* Photo strip */}
                    <div className="flex flex-wrap items-center gap-2">
                        {attachments.length
                            ? attachments.map((a) => (
                                  <button
                                      key={a.id}
                                      type="button"
                                      onClick={() => setPreviewImage(pickAttachmentSignedUrl(a))}
                                      className="relative h-16 w-16 overflow-hidden rounded-md border border-zinc-200 dark:border-zinc-700"
                                      data-testid={`${testidPrefix}-photo-${a.id}`}
                                  >
                                      <img
                                          src={pickAttachmentSignedUrl(a)}
                                          alt={a.caption || item.label}
                                          className="h-full w-full object-cover"
                                      />
                                  </button>
                              ))
                            : attachmentIds.map((aid) => (
                                  <div
                                      key={aid}
                                      className="flex h-16 w-16 items-center justify-center rounded-md border border-dashed border-zinc-300 text-[10px] text-zinc-500 dark:border-zinc-700"
                                      data-testid={`${testidPrefix}-photo-${aid}`}
                                  >
                                      Photo
                                  </div>
                              ))}
                        {canWrite && !locked && photoCount < MAX_PHOTOS_PER_FINDING ? (
                            <label
                                className={cn(
                                    "flex h-16 w-16 cursor-pointer flex-col items-center justify-center rounded-md border border-dashed text-[10px] text-zinc-500",
                                    "border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800",
                                    uploading && "cursor-wait opacity-60",
                                )}
                                data-testid={`${testidPrefix}-add-photo`}
                            >
                                {uploading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Camera className="h-4 w-4" />
                                )}
                                <span>Photo</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    multiple
                                    className="sr-only"
                                    onChange={handlePhotoUpload}
                                />
                            </label>
                        ) : null}
                    </div>

                    {isYellowRed && canConvert ? (
                        <div className="flex justify-end pt-1">
                            <Button
                                type="button"
                                size="sm"
                                variant={alreadyConverted ? "outline" : "default"}
                                onClick={handleConvert}
                                disabled={alreadyConverted || converting}
                                data-testid={`${testidPrefix}-convert-btn`}
                                className={cn(
                                    "gap-1.5",
                                    alreadyConverted && "text-emerald-700 dark:text-emerald-400",
                                )}
                            >
                                {converting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Receipt className="h-3 w-3" />}
                                {alreadyConverted ? "Added to estimate ✓" : "Convert to estimate line"}
                            </Button>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {previewImage ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setPreviewImage(null)}
                    data-testid={`${testidPrefix}-lightbox`}
                >
                    <img
                        src={previewImage}
                        alt={item.label}
                        className="max-h-full max-w-full rounded-lg object-contain"
                    />
                </div>
            ) : null}
        </div>
    );
}
